import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupAccordionListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        // const openDetails = this.querySelectorAll('details[open]');
        // openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Setup accordion functionality for child menus
   * アコーディオン機能のセットアップ
   */
  #setupAccordionListeners() {
    // Find all accordion elements
    const accordions = this.querySelectorAll('.menu-drawer__accordion');
    
    accordions.forEach(accordion => {
      const summary = accordion.querySelector('summary');
      if (!summary) return;

      // Add click event listener for accordion toggle
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        
        const isOpen = accordion.hasAttribute('open');
        
        if (isOpen) {
          // Close accordion
          accordion.removeAttribute('open');
          summary.setAttribute('aria-expanded', 'false');
        } else {
          // Open accordion
          accordion.setAttribute('open', '');
          summary.setAttribute('aria-expanded', 'true');
        }
      });

      // Set initial aria-expanded state
      const isInitiallyOpen = accordion.hasAttribute('open');
      summary.setAttribute('aria-expanded', isInitiallyOpen.toString());
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

/**
 * Custom accordion element for menu drawer
 * メニュードロワー用のカスタムアコーディオン要素
 */
class AccordionCustom extends HTMLElement {
  connectedCallback() {
    this.setupAccordion();
  }

  setupAccordion() {
    const details = this.querySelector('details');
    const summary = details?.querySelector('summary');
    
    if (!details || !summary) return;

    // Ensure accordion starts closed
    details.removeAttribute('open');
    summary.setAttribute('aria-expanded', 'false');

    // Add click handler
    summary.addEventListener('click', (event) => {
      event.preventDefault();
      
      const isOpen = details.hasAttribute('open');
      
      if (isOpen) {
        details.removeAttribute('open');
        summary.setAttribute('aria-expanded', 'false');
      } else {
        details.setAttribute('open', '');
        summary.setAttribute('aria-expanded', 'true');
      }
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
  
  // Also reset any accordion elements inside
  // const accordions = element.querySelectorAll('.menu-drawer__accordion');
  // accordions.forEach(accordion => {
  //   accordion.removeAttribute('open');
  //   const summary = accordion.querySelector('summary');
  //   if (summary) {
  //     summary.setAttribute('aria-expanded', 'false');
  //   }
  // });
}